import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, TextField, IconButton,
  Table, TableHead, TableRow, TableCell, TableBody,
  MenuItem, CircularProgress, Pagination,
  useMediaQuery, useTheme,
} from "@mui/material";
import SearchIcon             from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FilterListIcon         from "@mui/icons-material/FilterList";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import api from "../../services/api";

function formatRupiah(value) {
  if (!value && value !== 0) return "Rp 0";
  const num = parseFloat(value);
  if (num >= 1_000_000_000) return `Rp ${(num / 1_000_000_000).toFixed(1)} M`;
  if (num >= 1_000_000)     return `Rp ${(num / 1_000_000).toFixed(1)} Jt`;
  if (num >= 1_000)         return `Rp ${(num / 1_000).toFixed(0)} Rb`;
  return `Rp ${num.toLocaleString("id-ID")}`;
}

const statusConfig = {
  pending:   { label: "Pending",   color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
  disetujui: { label: "Disetujui", color: "#059669", bg: "#f0fdf4", border: "#bbf7d0" },
  ditolak:   { label: "Ditolak",   color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
};

const typeConfig = {
  biasa:    { label: "Biasa",    color: "#64748b", bg: "#f1f5f9", border: "#cbd5e1" },
  segera:   { label: "Segera",   color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
  mendesak: { label: "Mendesak", color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
};

function getTypeConfig(type) {
  if (!type) return typeConfig.biasa;
  return typeConfig[type.toLowerCase()] || typeConfig.biasa;
}

function getStatusConfig(status) {
  if (!status) return { label: "-", color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb" };
  return statusConfig[status.toLowerCase()] || { label: status, color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb" };
}

function StatusBadge({ config }) {
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center",
      background: config.bg, border: `1px solid ${config.border}`,
      borderRadius: "6px", px: 1, py: 0.3,
    }}>
      <Typography sx={{ fontSize: "0.68rem", color: config.color, fontWeight: 600 }}>
        {config.label}
      </Typography>
    </Box>
  );
}

function extractList(resData) {
  if (Array.isArray(resData))             return resData;
  if (Array.isArray(resData?.data))       return resData.data;
  if (Array.isArray(resData?.data?.data)) return resData.data.data;
  return [];
}

const ITEMS_PER_PAGE = 10;

export default function UserListProcurement() {
  const navigate = useNavigate();
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const user = (() => {
    try { return JSON.parse(localStorage.getItem("user")); }
    catch { return null; }
  })();

  const [data,         setData]         = useState([]);
  const [filtered,     setFiltered]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter,   setTypeFilter]   = useState("all");
  const [page,         setPage]         = useState(1);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res    = await api.get("/procurements");
        const list   = extractList(res.data);
        const myList = list.filter((p) => p.user_id === user?.id || p.user?.id === user?.id);
        setData(myList);
        setFiltered(myList);
      } catch (err) {
        setError("Gagal memuat data pengajuan.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    let result = [...data];
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (p) => p.request_number?.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== "all") {
      result = result.filter((p) => p.status?.name?.toLowerCase() === statusFilter);
    }
    if (typeFilter !== "all") {
      result = result.filter((p) => (p.request_type || "biasa").toLowerCase() === typeFilter);
    }
    setFiltered(result);
    setPage(1);
  }, [search, statusFilter, typeFilter, data]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated  = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  const hasFilter  = search || statusFilter !== "all" || typeFilter !== "all";

  const handleReset = () => { setSearch(""); setStatusFilter("all"); setTypeFilter("all"); };

  return (
    <Box>
      {/* ── HEADER ── */}
      <Box sx={{ mb: 4 }}>
        <Typography sx={{ fontSize: "0.7rem", color: "#9ca3af", fontWeight: 500, mb: 0.4 }}>
          Riwayat Pengajuan
        </Typography>
        <Typography sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" }, fontWeight: 800, color: "#111827", letterSpacing: "-0.02em", lineHeight: 1.2 }}>
          Pengajuan Sarana & Prasarana
        </Typography>
        <Typography sx={{ fontSize: "0.75rem", color: "#6b7280", mt: 0.3 }}>
          Semua pengajuan pengadaan yang kamu buat
        </Typography>
      </Box>

      {/* ── FILTER ── */}
      <Box sx={{ border: "1px solid #e5e7eb", borderRadius: "12px", p: { xs: 1.5, sm: 2 }, mb: 2, backgroundColor: "#fff" }}>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            size="small" placeholder="Cari no. pengajuan..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 16, color: "#9ca3af", mr: 0.5 }} /> }}
            sx={{ flex: "1 1 200px", "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.845rem", "& fieldset": { borderColor: "#e5e7eb" }, "&:hover fieldset": { borderColor: "#9ca3af" } } }}
          />
          <TextField
            select size="small" value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            InputProps={{ startAdornment: <FilterListIcon sx={{ fontSize: 16, color: "#9ca3af", mr: 0.5 }} /> }}
            sx={{ width: { xs: "48%", sm: 165 }, "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.845rem", "& fieldset": { borderColor: "#e5e7eb" }, "&:hover fieldset": { borderColor: "#9ca3af" } } }}
          >
            <MenuItem value="all">Semua Status</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="disetujui">Disetujui</MenuItem>
            <MenuItem value="ditolak">Ditolak</MenuItem>
          </TextField>
          <TextField
            select size="small" value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            sx={{ width: { xs: "48%", sm: 145 }, "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.845rem", "& fieldset": { borderColor: "#e5e7eb" }, "&:hover fieldset": { borderColor: "#9ca3af" } } }}
          >
            <MenuItem value="all">Semua Sifat</MenuItem>
            <MenuItem value="biasa">Biasa</MenuItem>
            <MenuItem value="segera">Segera</MenuItem>
            <MenuItem value="mendesak">Mendesak</MenuItem>
          </TextField>

          <Typography sx={{ fontSize: "0.78rem", color: "#9ca3af" }}>{filtered.length} pengajuan</Typography>

          {hasFilter && (
            <Box onClick={handleReset} sx={{ fontSize: "0.78rem", color: "#6b7280", cursor: "pointer", px: 1.2, py: 0.5, borderRadius: "6px", border: "1px solid #e5e7eb", "&:hover": { color: "#111827", borderColor: "#9ca3af" }, transition: "all 0.15s", whiteSpace: "nowrap" }}>
              Reset
            </Box>
          )}
        </Box>
      </Box>

      {error && (
        <Box sx={{ backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", p: 2, mb: 2 }}>
          <Typography sx={{ color: "#dc2626", fontSize: "0.845rem" }}>{error}</Typography>
        </Box>
      )}

      {/* ── DATA ── */}
      <Box sx={{ border: "1px solid #e5e7eb", borderRadius: "12px", overflow: "hidden", backgroundColor: "#fff" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={22} sx={{ color: "#d1d5db" }} />
          </Box>
        ) : paginated.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <AssignmentOutlinedIcon sx={{ fontSize: 32, color: "#d1d5db", mb: 1 }} />
            <Typography sx={{ color: "#6b7280", fontSize: "0.845rem", fontWeight: 600, mb: 0.3 }}>
              {hasFilter ? "Tidak ada yang sesuai filter." : "Belum ada pengajuan."}
            </Typography>
            {!hasFilter && <Typography sx={{ color: "#9ca3af", fontSize: "0.75rem" }}>Mulai ajukan kebutuhan unit kamu</Typography>}
          </Box>
        ) : isMobile ? (
          /* ── MOBILE CARDS ── */
          <Box>
            {paginated.map((p, i) => {
              const sc = getStatusConfig(p.status?.name);
              const tc = getTypeConfig(p.request_type);
              return (
                <Box
                  key={p.id}
                  onClick={() => navigate(`/user/procurement/${p.id}`)}
                  sx={{
                    px: 2, py: 2,
                    borderBottom: i < paginated.length - 1 ? "1px solid #f3f4f6" : "none",
                    cursor: "pointer", "&:active": { backgroundColor: "#f3f4f6" },
                    transition: "background 0.15s",
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.8 }}>
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#111827" }}>
                      {p.request_number || "-"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#111827", flexShrink: 0, ml: 1.5 }}>
                      {formatRupiah(p.total_amount)}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: "0.72rem", color: "#9ca3af", mb: 1 }}>
                    {p.request_date
                      ? new Date(p.request_date).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" })
                      : "-"}
                    {" · "}{p.items?.length ?? 0} item
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.8 }}>
                    <StatusBadge config={tc} />
                    <StatusBadge config={sc} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          /* ── DESKTOP TABLE ── */
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#fafafa" }}>
                  {["No", "No. Pengajuan", "Tanggal", "Item", "Total", "Sifat", "Status", "Aksi"].map((h) => (
                    <TableCell key={h} sx={{ fontSize: "0.6rem", fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: "1px solid #f3f4f6", py: 1.5, px: 2, whiteSpace: "nowrap" }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated.map((p, index) => {
                  const sc = getStatusConfig(p.status?.name);
                  const tc = getTypeConfig(p.request_type);
                  return (
                    <TableRow key={p.id} sx={{ "&:hover": { backgroundColor: "#fafafa" }, borderBottom: "1px solid #f9fafb", transition: "background 0.1s" }}>
                      <TableCell sx={{ px: 2, py: 1.6, color: "#9ca3af", fontSize: "0.75rem" }}>{(page - 1) * ITEMS_PER_PAGE + index + 1}</TableCell>
                      <TableCell sx={{ px: 2, py: 1.6 }}><Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#111827" }}>{p.request_number || "-"}</Typography></TableCell>
                      <TableCell sx={{ px: 2, py: 1.6, whiteSpace: "nowrap" }}><Typography sx={{ fontSize: "0.8rem", color: "#6b7280" }}>{p.request_date ? new Date(p.request_date).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }) : "-"}</Typography></TableCell>
                      <TableCell sx={{ px: 2, py: 1.6 }}><Typography sx={{ fontSize: "0.8rem", color: "#6b7280" }}>{p.items?.length ?? 0} item</Typography></TableCell>
                      <TableCell sx={{ px: 2, py: 1.6, whiteSpace: "nowrap" }}><Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#111827" }}>{formatRupiah(p.total_amount)}</Typography></TableCell>
                      <TableCell sx={{ px: 2, py: 1.6 }}><StatusBadge config={tc} /></TableCell>
                      <TableCell sx={{ px: 2, py: 1.6 }}><StatusBadge config={sc} /></TableCell>
                      <TableCell sx={{ px: 2, py: 1.6 }}>
                        <IconButton size="small" onClick={() => navigate(`/user/procurement/${p.id}`)} sx={{ color: "#6b7280", borderRadius: "6px", "&:hover": { backgroundColor: "#f3f4f6", color: "#111827" } }}>
                          <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}

        {!loading && totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", px: 2, py: 1.5, borderTop: "1px solid #f3f4f6" }}>
            <Typography sx={{ fontSize: "0.75rem", color: "#9ca3af", mr: 2 }}>Halaman {page} dari {totalPages}</Typography>
            <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} size="small"
              sx={{ "& .MuiPaginationItem-root": { borderRadius: "6px", fontSize: "0.75rem" }, "& .Mui-selected": { backgroundColor: "#111827 !important", color: "#fff" } }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
